import { IFilter } from '@/interfaces/common.interface';
import { logout } from '@/redux/features/UserSlice';

import { store } from '@/redux/store';
import React from 'react';
import { format, isAfter, parseISO } from 'date-fns';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { RoutesLink } from './RouteLinks';
import { UserRoles } from './userRole';
import { IRole } from '@/interfaces/Auth/auth.interface';
import { OrderStatus } from '@/interfaces/Order/order.interface';
import { ReviewStatus, ReviewTargetType } from '@/interfaces/Review/review.interface';
import { ReturnStatus } from '@/interfaces/ReturnOrders/returnOrder.interface';
import {
  Truck,
  Info,
  CheckCircle2,
  Package,
  Shield,
  Store,
} from 'lucide-react';
import moment from 'moment-timezone';
import { ICheckoutMessageForm } from '@/pages/Settings/CheckoutMessage';
import { IStore } from '@/interfaces/Store/store.interface';

/**
 * common function to set cookies in react application
 * @param name name of cookie
 * @param value value to set in cookie
 */
export const setCookies = (name: string, value: string | object) => {
  Cookies.set(name, typeof value === 'object' ? JSON.stringify(value) : value, { path: import.meta.env.VITE_PUBLIC_URL || '/' });
};

/**
 *
 * @param name find cookies by name
 * @returns
 */
export const getCookies = (name: string) => {
  const value = Cookies.get(name);
  if (!value) return undefined;
  try { return JSON.parse(value); } catch { return value; }
};

/**
 *
 * @param name Delete cookies by name
 * @returns
 */
export const deleteCookies = (name: string) => {
  Cookies.remove(name, { path: import.meta.env.VITE_PUBLIC_URL || '/' });
};

/**
 *
 * @returns reutn the saved auth token
 */
export const getToken = () => {
  const value = Cookies.get(import.meta.env.VITE_BUSINESS + 'token');
  if (!value) return undefined;
  try { return JSON.parse(value); } catch { return value; }
};

export function toFixedThree(input: string | number, fixedTo = 3) {
  return parseFloat(input.toString()).toFixed(fixedTo);
}

// export const toFixedThree = (price: any) => {
//   const amount = parseFloat(price);
//   const formattedPrice = new Intl.NumberFormat('en-BH', {
//     style: 'currency',
//     currency: 'BHD',
//     minimumFractionDigits: 3,
//   }).format(amount);
//   return formattedPrice;
// };

/**
 *
 * @param type type of toast notification
 * @param message message to show in toast notification
 */

// import { deleteToken } from 'firebase/messaging';
// import { messaging } from '@/firebase-messaging';

export const logoutUser = async () => {
  // try {
  //   const currentToken = await getToken();
  //   if (currentToken) {
  //     await deleteToken(messaging);
  //   }
  // } catch (error) {
  //   console.error("Error deleting FCM token:", error);
  // }
  deleteCookies(import.meta.env.VITE_BUSINESS + 'token');
  deleteCookies(import.meta.env.VITE_BUSINESS + 'role');
  deleteCookies(import.meta.env.VITE_BUSINESS + 'refresh_token');
  localStorage.removeItem(`${import.meta.env.VITE_BUSINESS}fcmToken`);
  window.location.href = import.meta.env.VITE_PUBLIC_URL || '/';
  setTimeout(() => {
    store.dispatch(logout());
  }, 1000);
};

export const showToastNotification = (
  type: 'success' | 'danger' | 'info' | 'default' | 'warning',
  message: string,
) => {
  const commonOptions = {
    position: 'top-right' as const,
    duration: 5000,
  };

  // Create toast with close button
  const createToastWithClose = (
    toastFn: typeof toast | typeof toast.success | typeof toast.error,
    icon?: string,
  ) => {
    return toastFn(
      (t) =>
        React.createElement(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            },
          },
          React.createElement('span', { style: { flex: 1 } }, message),
          React.createElement(
            'button',
            {
              onClick: () => toast.dismiss(t.id),
              style: {
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                opacity: 0.7,
                padding: '0',
                marginLeft: '8px',
              },
            },
            '✕',
          ),
        ),
      { ...commonOptions, icon },
    );
  };

  switch (type) {
    case 'success':
      return createToastWithClose(toast.success);

    case 'danger':
      return createToastWithClose(toast.error);

    case 'warning':
      return createToastWithClose(toast, '⚠️');

    case 'info':
      return createToastWithClose(toast);

    case 'default':
    default:
      return createToastWithClose(toast);
  }
};

let cachedDeviceId: string | null = null;

export const getDeviceId = (): string => {
  // Return the cached device ID if available
  if (cachedDeviceId !== null) {
    return cachedDeviceId;
  }

  // Attempt to retrieve an existing device ID from localStorage
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    // Use crypto.randomUUID() if available for secure UUID generation
    if (window.crypto && typeof crypto.randomUUID === 'function') {
      deviceId = crypto.randomUUID();
    } else {
      // Fallback: simple UUID generator
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        (c: string): string => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        },
      );
    }
    // Save the generated ID so that it persists across sessions
    localStorage.setItem('deviceId', deviceId);
  }

  // Cache the ID in memory for subsequent call
  cachedDeviceId = deviceId;
  return deviceId;
};

export const statusColors = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'online':
    case 'active':
      return 'green-500';
    case 'offline':
    case 'inactive':
      return 'red-500';
    case 'created':
    case 'pending':
      return 'blue-500';
    default:
      return 'gray-500';
  }
};

export const formatDateTime = (
  date: string | null | undefined | Date,
  type = 'default',
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string => {
  if (!date) return '';

  const formattedDate: Record<string, string> = {
    default: moment.utc(date).tz(timezone).format('DD MMM YYYY, HH:mm'),
    YYMMDD: moment.utc(date).tz(timezone).format('DD MMM YYYY'),
  };

  return formattedDate[type] || '';
};

// export const GetFilterUrl = (filter: IFilter) => {
//   return filter
//     ? Object?.entries(filter)
//         ?.filter(([, value]) => value)
//         .map(
//           ([key, value]) =>
//             `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
//         )
//         .join('&')
//     : '';
// };

export const checkIsExpire = (date: string | Date | null): boolean => {
  if (date) {
    const today = new Date();
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    return isAfter(inputDate, today);
  } else {
    return false;
  }
};

export const range = (start: number, end: number) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export function formatStatus(status: string): string {
  if (!status) return '';
  if (status.toLowerCase() === 'takeaway') return 'Collection';
  return status
    .toLowerCase() // convert to lowercase
    .replace(/_/g, ' ') // replace underscores with spaces
    .replace(/^\w/, (c) => c.toUpperCase()) // capitalize the first letter
    .replace(/\bof\b/g, 'of'); // keep 'of' lowercase
}

export const getPaymentMethodMessage = (method: string): string => {
  const paymentMethodMap: Record<string, string> = {
    cod: 'Cash on Delivery',
    wallet: 'Paid via Wallet',
    card: 'Paid via Credit Card',
    apple_pay: 'Paid via Apple Pay',
    benefitpay: 'Paid via BenefitPay',
    'bh.benefit': 'Paid via Debit Card',
  };

  const key = method.toLowerCase();
  return paymentMethodMap[key] || 'Unknown Payment Method';
};

export const getUserType = (type: string, isEmployee = false): string => {
  if (isEmployee) {
    return 'Employee';
  }
  const userTypes: Record<string, string> = {
    user: 'User',
    driver: 'Driver',
    employee: 'Employee',
  };
  const key = type && type.toLowerCase();
  return userTypes[key] || type || 'unkown type';
};

/**
 * Yebo Eggs — resources that can be granted permissions.
 * Must stay in sync with RESOURCES in src/context/permissions.ts
 */
export const RolesType = [
  'store',
  'category',
  'product',
  'variant',
  'addon',
  'order',
  'discount',
  'coupon',
  'driver',
  'customer',
  'notification',
  'settings',
  'live-order',
  'staff',
  'role',
  'review',
  'tag',
  'cash-settlement',
  'store-settlement',
  'return',
  'payout',
  'zone',
];

/** Map of internal resource keys to display labels */
export const ResourceLabelMap: Record<string, string> = {
  'store': 'Store',
  'category': 'Category',
  'product': 'Product',
  'variant': 'Variant',
  'addon': 'Addon',
  'order': 'Order',
  'discount': 'Discount',
  'coupon': 'Coupon',
  'driver': 'Driver',
  'customer': 'Customer',
  'notification': 'Notification',
  'settings': 'Settings',
  'live-order': 'Live Order',
  'staff': 'Staff',
  'role': 'Role',
  'review': 'Review',
  'tag': 'Tag',
  'cash-settlement': 'Cash Settlement',
  'store-settlement': 'Store Settlement',
  'dashboard': 'Dashboard',
  'home-layout': 'Home Layout',
  'return': 'Return Order',
  'payout': 'Payout',
  'return-reason': 'Return Reason',
  'zone': 'Zone'
};

/** CRUD actions matching ACTIONS in src/context/permissions.ts */
export const RolesPermission = ['view', 'create', 'update', 'delete', 'export'];

const customActions: Record<string, string[]> = {
  'live-order': ['view'],
  'notification': ['view', 'create'],
  'store-settlement': ['view', 'create', 'approved'],
  'store': ['view', 'create', 'update', 'export'],
  'order': ['view', 'update', 'export'],
  'review': ['view', 'delete', 'export'],
  'cash-settlement': ['view', 'create'],
  "return": ["view", "update", 'export'],
  "product": ['view', 'create', 'update', 'delete', 'import-product', 'add-stock', 'export'],
  "addon": ['view', 'create', 'update', 'delete', 'add-stock'],
  "variant": ['view', 'create', 'update', 'delete', 'add-stock'],
  "customer": ["view", "block", 'export'],
  "driver": ['view', 'create', 'update', "approved-reject", 'export'],
  "payout": ['view', 'approved-reject', 'export'],
  'settings': ["view", "admin-settings", 'store-settings']
};

export const RolesTypes = RolesType.map((resource) => ({
  resource,
  actions: customActions[resource] || RolesPermission,
}));

export const formatPlaceholder = (type: string | undefined | null) => {
  const types: Record<string, string> = {
    day: 'Today',
    month: 'This Month',
    week: 'This Week',
    Year: 'This Year',
  };
  if (type) {
    return types[type];
  } else {
    return '';
  }
};

export const GetFilterUrl = (filter: IFilter) => {
  return filter
    ? Object?.entries(filter)
      ?.filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value as string | number | boolean)}`,
      )
      .join('&')
    : '';
};

// Helper function to format stat labels (camelCase to Title Case)
export const formatLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // replace underscores with spaces
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

export const formatRand = (
  value: number | string | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') return 'R0.00';

  const amount: number = typeof value === 'string' ? Number(value) : value;

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(isNaN(amount) ? 0 : amount);
};

export const firstLetterUpperCase = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn(
        'Clipboard API failed, falling back to legacy execution',
        err,
      );
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed'; // Avoid scrolling
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error('Fallback clipboard copy failed', err);
    return false;
  }
};
export const getPrefixByRole = (role: string) => {
  if (role === UserRoles.admin) return '/panel';
  return '';
};

export const getRedirectPath = (role: string) => {
  if (role === UserRoles.admin) {
    return '/panel/dashboard';
  }
  return RoutesLink.dashboard;
};

export const timeAgo = (dateString: string) => {
  const now = new Date();
  const past = new Date(dateString);
  const seconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;

  return past.toLocaleDateString();
};

export const getRole = (rolesMap: Record<string, IRole>, userRoleId: string) => {
  const activeRole = rolesMap
    ? (Object.values(rolesMap) as IRole[]).find((r) => r.id === userRoleId)
    : undefined;
  return activeRole;
}

/**
 * Formats a 24-hour time string ("HH:mm:ss" or "HH:mm") into a 12-hour AM/PM format
 * E.g. "14:30:00" -> "2:30 PM", "09:00:00" -> "9:00 AM"
 */
export const formatTime12Hour = (timeString: string | null | undefined): string => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  if (parts.length < 2) return timeString; // fallback if format is unexpected

  const hourStr = parts[0];
  const minuteStr = parts[1];

  let hour = parseInt(hourStr, 10);
  if (isNaN(hour)) return timeString;

  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  hour = hour ? hour : 12; // handle midnight (0) as 12

  return `${hour}:${minuteStr} ${ampm}`;
};

export const getInitials = (name?: string) => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
};


export const parseOrderAddress = (address: any): any | null => {
  if (!address) return null;
  if (typeof address === 'object') return address;
  try {
    const parsed = JSON.parse(address);
    return typeof parsed === 'object' ? parsed : address;
  } catch {
    return address;
  }
};


// ── Order helpers (JSX-based) ────────────────────────────────────
export { getOrderFlow, getStatusIndex } from './orderHelper';

export const columnColors: Record<OrderStatus, string> = {
  pending: 'border-t-info',
  accepted: 'border-t-info',
  cancelled: 'border-t-warning',
  failed: 'border-t-success',
  completed: 'border-t-info',
  ready_for_pickup: 'border-t-success',
  picked_up: 'border-t-success',
  in_progress: 'border-t-warning',
  out_for_delivery: 'border-t-success',
  delivered: 'border-t-success',
  delayed: 'border-t-warning',
  refund_requested: 'border-t-warning',
  refund_in_progress: 'border-t-warning',
  refunded: 'border-t-success',
  refund_rejected: 'border-t-warning',
  driver_assigned: 'border-t-info',
  arrived: 'border-t-success',
  destination_arrived: 'border-t-success',
};


export const TARGET_TYPE_OPTIONS: { label: string; value: ReviewTargetType | '' }[] = [
  { label: 'All Types', value: '' },
  { label: 'Product', value: 'on_product' },
  // { label: 'Store', value: 'on_store' },
  { label: 'Driver', value: 'on_driver' },
  // { label: 'Order', value: 'on_order' },
];

export const STATUS_OPTIONS: { label: string; value: ReviewStatus | '' }[] = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

/**
 * Extracts granular address components from Google Maps GeocoderAddressComponent array
 * Mappings:
 * - postcode: postal_code
 * - suburb: sublocality_level_1 or sublocality
 * - area: sublocality_level_2 or neighborhood
 * - locality: locality (City/Town)
 * - city: administrative_area_level_2 (Municipality/City)
 * - state: administrative_area_level_1 (Province)
 * - country: country
 */
export const extractAddressComponents = (components: google.maps.GeocoderAddressComponent[]) => {
  const getComponent = (types: string[]) => {
    return components.find(c => types.some(t => c.types.includes(t)))?.long_name || '';
  };

  return {
    postcode: getComponent(['postal_code']),
    suburb: getComponent(['sublocality_level_1', 'sublocality']),
    area: getComponent(['sublocality_level_2', 'neighborhood']),
    locality: getComponent(['locality']),
    city: getComponent(['administrative_area_level_2', 'locality']),
    state: getComponent(['administrative_area_level_1']),
    country: getComponent(['country']),
  };
};

/**
 * Generates a URL-friendly slug from a given string
 * @param text The string to slugify
 * @returns A formatted slug string
 */
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
};

// ── Mapping of sequential steps in the return timeline progress ──
export const returnSteps = [
  {
    status: ReturnStatus.PENDING_REVIEW,
    label: 'Review',
    icon: React.createElement(Info, { className: "w-4 h-4" })
  },
  {
    status: ReturnStatus.PICKUP_ASSIGNED,
    label: 'Pickup Assigned',
    icon: React.createElement(Truck, { className: "w-4 h-4" })
  },
  {
    status: ReturnStatus.RETURN_COLLECTED,
    label: 'Collected',
    icon: React.createElement(Package, { className: "w-4 h-4" })
  },
  {
    status: ReturnStatus.RE_ARRIVED_AT_STORE,
    label: 'At Store',
    icon: React.createElement(Store, { className: "w-4 h-4" })
  },
  {
    status: ReturnStatus.RETURN_DELIVERED,
    label: 'Delivered to Store',
    icon: React.createElement(Shield, { className: "w-4 h-4" })
  },
  {
    status: ReturnStatus.APPROVED,
    label: 'Refund Completed',
    icon: React.createElement(CheckCircle2, { className: "w-4 h-4" })
  }
];

export const getReturnStatusIndex = (status: ReturnStatus): number => {
  switch (status) {
    case ReturnStatus.PENDING_REVIEW:
      return 0;
    case ReturnStatus.PICKUP_ASSIGNED:
      return 1;
    case ReturnStatus.RE_ROUTE_TO_CUSTOMER:
    case ReturnStatus.RE_ARRIVED_AT_CUSTOMER:
    case ReturnStatus.RETURN_COLLECTED:
      return 2;
    case ReturnStatus.RE_ROUTE_TO_STORE:
    case ReturnStatus.RE_ARRIVED_AT_STORE:
      return 3;
    case ReturnStatus.RETURN_DELIVERED:
      return 4;
    case ReturnStatus.APPROVED:
      return 5;
    default:
      return 0;
  }
};

/**
 * Returns an appropriate Google Maps zoom level to fit a given radius (km) in the map viewport.
 * @param km - Radius in kilometres
 * @returns zoom level (number)
 */
export const getZoomForRadius = (km: number): number => {
  if (!km || km <= 0) return 15;
  if (km <= 1) return 14;
  if (km <= 2) return 13;
  if (km <= 5) return 12;
  if (km <= 10) return 11;
  if (km <= 20) return 10;
  if (km <= 40) return 9;
  return 8;
};


export const isTodayHoliday = (holidaySettings: ICheckoutMessageForm) => {
  if (!holidaySettings || holidaySettings.status !== 'active') return false;

  const now = new Date();
  const start = new Date(`${holidaySettings.startDate}T${holidaySettings.startTime}`);
  const end = new Date(`${holidaySettings.endDate}T${holidaySettings.endTime}`);

  return now >= start && now <= end;
};

export const isStoreClosed = (store: IStore) => {
  const openingTime = store?.settings?.openingTime;
  const closingTime = store?.settings?.closingTime;
  if (!openingTime || !closingTime) return false;
  const now = moment().format('HH:mm:ss');
  const isClosed = now < openingTime || now > closingTime;
  return isClosed;
}

export const downloadExcel = async (url: string, filename: string) => {
  const token = getToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'Failed to download Excel file', statusCode: response.status };
    }
    throw errorData;
  }

  const rawData = await response.arrayBuffer();
  const blob = new Blob([rawData], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();

  URL.revokeObjectURL(link.href);
};

export const downloadPdf = async (url: string, filename?: string) => {
  const token = getToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'Failed to download PDF invoice', statusCode: response.status };
    }
    throw errorData;
  }

  const rawData = await response.arrayBuffer();
  const blob = new Blob([rawData], {
    type: 'application/pdf',
  });

  const blobUrl = URL.createObjectURL(blob);

  if (filename) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.click();
  }

  return blobUrl;
};

export const formatChartDate = (dateString: string) => {
  if (!dateString) return '';
  try {
    // YYYY-MM-DD format (e.g., "2026-07-02")
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // parseISO treats YYYY-MM-DD as local midnight, avoiding timezone shifting issues that new Date() might have in some browsers for short ISO strings. Wait, parseISO treats YYYY-MM-DD as UTC. Actually new Date(Y, M, D) is safest, but we can just split.
      const [y, m, d] = dateString.split('-');
      const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      return format(dateObj, 'MMM d');
    }
    // YYYY-MM format (e.g., "2026-07")
    if (dateString.match(/^\d{4}-\d{2}$/)) {
      const [y, m] = dateString.split('-');
      const dateObj = new Date(parseInt(y), parseInt(m) - 1, 1);
      return format(dateObj, 'MMM yyyy');
    }
    // If it's already HH:mm or Day string (Mon, Tue)
    return dateString;
  } catch (e) {
    return dateString;
  }
};

export interface IUserBasicName {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

export const getUserFullName = (user?: IUserBasicName | null, fallback = 'N/A'): string => {
  if (!user) return fallback;
  if (user.fullName && user.fullName.trim()) return user.fullName.trim();
  const constructed = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (constructed) return constructed;
  if (user.name && user.name.trim()) return user.name.trim();
  return fallback;
};

export const getEffectiveExtendedRadius = (deliveryKm: number, extendedKm: number): number => {
  if (!extendedKm || extendedKm <= 0) return 0;
  if (extendedKm <= deliveryKm) {
    return deliveryKm + 1;
  }
  return extendedKm;
};


